import { DataProxy, FetchResult } from "@apollo/client";
import React, { useState } from "react";
import {
  CreateFoodGroupInput,
  CreateOrgFoodGroupMutation,
  CreateOrgFoodGroupMutationFn,
  CreateSharedFoodGroupMutation,
  CreateSharedFoodGroupMutationFn,
  EditFoodGroupInput,
  EditOrgFoodGroupMutationFn,
  EditSharedFoodGroupMutationFn,
  FullFoodGroupFragment,
} from "../../types";
import { PaginationHooks } from "../../utils/pagination";
import { useSnackbar } from "../Snackbar/SnackbarContext";
import FoodGroupModalEdit from "./Modal/Edit";
import FoodGroupModalNew from "./Modal/New";
import { FoodGroupTable, IFoodGroup } from "./Table";
import { UpdateCacheCreateFoodGroup } from "./utils";

export interface FoodGroupDashboardProps {
  foodGroups: readonly FullFoodGroupFragment[];
  paginationHooks: PaginationHooks;
  total: number;
  setSortOrder: (sortOrder: "ASC" | "DESC") => void;
  sortOrder: "ASC" | "DESC";
}

interface EditableFoodGroupDashboardProps extends FoodGroupDashboardProps {
  createFoodGroup: CreateSharedFoodGroupMutationFn | CreateOrgFoodGroupMutationFn;
  updateCacheCreateFoodGroup: ({ cache, mutationResult }: UpdateCacheCreateFoodGroup) => void;
  editFoodGroup: EditSharedFoodGroupMutationFn | EditOrgFoodGroupMutationFn;
}

interface ReadOnlyFoodGroupDashboardProps extends FoodGroupDashboardProps {
  readOnly: boolean;
}

const isReadOnly = (props: EditableFoodGroupDashboardProps | ReadOnlyFoodGroupDashboardProps): props is ReadOnlyFoodGroupDashboardProps => {
  return (props as ReadOnlyFoodGroupDashboardProps).readOnly !== undefined;
};

export const FoodGroupDashboard = (props: ReadOnlyFoodGroupDashboardProps | EditableFoodGroupDashboardProps) => {
  const { setMessage } = useSnackbar();
  const [selectedFoodGroup, setSelectedFoodGroup] = useState<IFoodGroup | null>(null);
  const [foodGroupModalOpen, setFoodGroupModalOpen] = useState(false);
  const { foodGroups, paginationHooks, total, setSortOrder, sortOrder } = props;

  if (isReadOnly(props)) {
    return (
      <>
        <FoodGroupTable
          readOnly
          setFoodGroupModalOpen={setFoodGroupModalOpen}
          setSelectedFoodGroup={setSelectedFoodGroup}
          rows={foodGroups.map(b => ({ ...b, foodCount: b.foods.length }))}
          paginationHooks={paginationHooks}
          total={total}
          setSortOrder={setSortOrder}
          sortOrder={sortOrder}
        />
        {foodGroupModalOpen && selectedFoodGroup && (
          <FoodGroupModalEdit
            readOnly
            open={!!foodGroupModalOpen}
            onClose={() => {
              setSelectedFoodGroup(null);
              setFoodGroupModalOpen(false);
            }}
            foodGroupId={selectedFoodGroup.id}
          />
        )}
      </>
    );
  } else {
    const { createFoodGroup, updateCacheCreateFoodGroup, editFoodGroup } = props;

    // Handlers
    const onCreateFoodGroup = async ({ name, source, foodIds }: CreateFoodGroupInput) => {
      const input = {
        name,
        source,
        foodIds,
      };
      createFoodGroup({
        variables: { input },
        update: (cache: DataProxy, mutationResult: FetchResult<CreateOrgFoodGroupMutation | CreateSharedFoodGroupMutation>) => {
          updateCacheCreateFoodGroup({ cache, mutationResult });
          setMessage("success", `Success! Created Food Group '${name}.'`);
          setSelectedFoodGroup(null);
          setFoodGroupModalOpen(false);
        },
      });
    };

    const onEditFoodGroup = async ({ id, foodGroup }: EditFoodGroupInput) => {
      const input = {
        id,
        foodGroup,
      };
      editFoodGroup({
        variables: { input },
        update: () => {
          setMessage("success", `Success! Updated Food Group '${foodGroup.name}.'`);
          setSelectedFoodGroup(null);
          setFoodGroupModalOpen(false);
        },
      });
    };

    return (
      <>
        <FoodGroupTable
          setFoodGroupModalOpen={setFoodGroupModalOpen}
          setSelectedFoodGroup={setSelectedFoodGroup}
          rows={foodGroups.map(b => ({ ...b, foodCount: b.foods.length }))}
          paginationHooks={paginationHooks}
          total={total}
          setSortOrder={setSortOrder}
          sortOrder={sortOrder}
        />
        {foodGroupModalOpen &&
          (selectedFoodGroup && selectedFoodGroup.id ? (
            <FoodGroupModalEdit
              open={!!foodGroupModalOpen}
              onClose={() => {
                setSelectedFoodGroup(null);
                setFoodGroupModalOpen(false);
              }}
              onEditFoodGroup={onEditFoodGroup}
              foodGroupId={selectedFoodGroup.id}
            />
          ) : (
            <FoodGroupModalNew
              open={!!foodGroupModalOpen}
              onClose={() => {
                setSelectedFoodGroup(null);
                setFoodGroupModalOpen(false);
              }}
              onCreateFoodGroup={onCreateFoodGroup}
            />
          ))}
      </>
    );
  }
};
